import { createSlice } from "@reduxjs/toolkit";
import { getEffectivePrice } from "../../utils/priceUtils";

const propertySlice = createSlice({
    name: 'property',
    initialState: {
        properties: [],
        property: null,
        editProperty: null,
        extra: {
            state: null
        },
        catalogProducts: [],
        propertySheet: {
            propertyId: "",
            rooms: [],
            buyerTotal: 0,
        },
        selectedRoom: null,
    },
    reducers: {
        setProperties: (state, action) => {
            state.properties = action.payload;
        },
        setProperty: (state, action) => {
            state.property = action.payload;
        },
        setEditProperty: (state, action) => {
            state.editProperty = action.payload;
        },
        setCatalogProducts: (state, action) => {
            state.catalogProducts = action.payload;
        },
        setPropertySheet: (state, action) => {
            state.propertySheet = action.payload;
        },
        addRoom: (state, action) => {
            state.propertySheet.rooms.push(action.payload);
        },
        setSelectedRoom: (state, action) => {
            state.selectedRoom = action.payload;
        },
        addProduct: (state, action) => {
            const { name, product } = action.payload;
            const room = state.propertySheet.rooms.find(room => room.name === name);
            const qty = Number(product.quantity) || 1;
            const effectivePrice = getEffectivePrice(product);
            const productCost = effectivePrice * qty;
            if (!room) {
                state.propertySheet.rooms.push({
                    name,
                    items: [product],
                    cost: productCost,
                    itemsCount: qty
                });
                state.propertySheet.buyerTotal = Number(state.propertySheet.buyerTotal) + productCost;
                return;
            }

            if (!room.items) room.items = [];

            const existingProduct = room.items.find(p => p.id === product.id);
            if (existingProduct) {
                const addedCost = effectivePrice * qty;
                let newProduct = {
                    ...existingProduct,
                    quantity: existingProduct.quantity + qty,
                };
                room.items = room.items.map(p => p.id === newProduct.id ? newProduct : p);
                room.cost = (Number(room.cost) || 0) + addedCost;
                room.itemsCount = (Number(room.itemsCount) || 0) + qty;
                state.propertySheet.buyerTotal = Number(state.propertySheet.buyerTotal) + addedCost;
                return;
            }
            room.items.push(product);
            room.itemsCount = (Number(room.itemsCount) || 0) + qty;
            room.cost = (Number(room.cost) || 0) + productCost;
            state.propertySheet.buyerTotal = Number(state.propertySheet.buyerTotal) + productCost;
        },
        updateProduct: (state, action) => {
            const { name, id, updatedData } = action.payload;
            
            const room = state.propertySheet.rooms.find(room => room.name === name);
            if (!room || !room.items) return;
        
            const productIndex = room.items.findIndex(product => product.id === id);
            if (productIndex === -1) return;

            const oldProduct = room.items[productIndex];
            const oldEffectivePrice = getEffectivePrice(oldProduct);
            const oldCost = oldEffectivePrice * Number(oldProduct.quantity || 1);
            
            room.items[productIndex] = {
                ...oldProduct,
                ...updatedData
            };

            const updatedProduct = room.items[productIndex];
            const newEffectivePrice = getEffectivePrice(updatedProduct);
            const newCost = newEffectivePrice * Number(updatedProduct.quantity || 1);
            
            const newRoomCost = Math.max(0, Number(room.cost) - oldCost + newCost);
            room.cost = newRoomCost;
            
            state.propertySheet.buyerTotal = Math.max(0, Number(state.propertySheet.buyerTotal) - oldCost + newCost);

            if (updatedData.quantity !== undefined) {
                room.itemsCount = room.items.reduce((total, item) => total + Number(item.quantity || 1), 0);
            }
        },
        addImage: (state, action) => {
            const { name, image } = action.payload;

            if (!state.propertySheet.rooms || !state.propertySheet.rooms.length > 0) return;

            if (!name || !image) return;
            if (!image.src) return;

            // get other room images and 1+ with id

            image.id = state.propertySheet.rooms.reduce((acc, room) => {
                if (!room.images || room.images.length === 0) return acc;
                return room.images.reduce((innerAcc, image) => {
                    const imageId = parseInt(image.id, 10); // Ensure id is a number
                    return Math.max(innerAcc, imageId);
                }, acc);
            }, 0) + 1;

            const room = state.propertySheet.rooms.find(room => room.name === name);
            if (!room) {
                state.propertySheet.rooms.push({
                    name,
                    images: [image]
                });
                return;
            }

            if (!room.images) room.images = [];
            room.images.push(image);
        },

        rearrangeImages: (state, action) => {
            const { name, images } = action.payload;
            if (!name || !images) return;

            const room = state.propertySheet.rooms.find(room => room.name === name);
            if (!room) return;

            room.images = images;
        },

        removeImage: (state, action) => {
            const { name, id } = action.payload;
            
            if (!state.propertySheet.rooms || !state.propertySheet.rooms.length > 0) return;
            if (!name || !id) return;

            const room = state.propertySheet.rooms.find(room => room.name === name);
            if (!room) return;

            if (!room.images) return;

            room.images = room.images.filter(image => image.id !== id);
        },

        removeProduct: (state, action) => {
            const { name, id } = action.payload;
            const room = state.propertySheet.rooms.find(room => room.name === name);
            if (!room) return;

            if (!room.items) return;

            const product = room.items.find(product => product.id === id);
            if (!product) return;
            
            const effectivePrice = getEffectivePrice(product);
            
            if (product.quantity > 1) {
                product.quantity -= 1;
                const newRoomCost = Math.max(0, room.cost - effectivePrice);
                room.cost = newRoomCost;
                room.itemsCount = room.itemsCount - 1 || 0;
                state.propertySheet.buyerTotal = Math.max(0, Number(state.propertySheet.buyerTotal) - effectivePrice);
                return;
            }

            const productCost = effectivePrice * Number(product.quantity);
            room.items = room.items.filter(product => product.id !== id);
            const newRoomCost = Math.max(0, room.cost - productCost);
            room.cost = newRoomCost;
            room.itemsCount = room.itemsCount - Number(product.quantity) || 0;
            state.propertySheet.buyerTotal = Math.max(0, Number(state.propertySheet.buyerTotal) - productCost);
        },
        setState: (state, action) => {
            state.extra = {
                state: action.payload
            }
        }
    },
});

export const {
    setProperties, setProperty, setEditProperty, setCatalogProducts,
    setPropertySheet, addRoom, addProduct, updateProduct, removeProduct, setSelectedRoom,
    setState,
    addImage,
    removeImage,
    rearrangeImages
} = propertySlice.actions;

export default propertySlice.reducer;


